import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Badge, Table, Button, Modal, Form,
  Spinner, Alert,
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSyncAlt, faFilter,
  faArrowDown, faArrowUp, faExchangeAlt, faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import {
  getPrimaNota, creaScritturaContabile, aggiornaScritturaContabile,
  eliminaScritturaContabile, getRiepilogoPrimaNota,
  getFireflyAccountMapping, getBanche,
} from '../../services/api';

const ANNO_CORRENTE = new Date().getFullYear();
const fmt = (v) => `€ ${parseFloat(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('it-IT') : '—';

const TIPI = [
  { value: 'prelievo_socio',      label: 'Prelievo socio',       dir: 'uscita',  icon: faArrowDown,    color: '#dc3545' },
  { value: 'conferimento_socio',  label: 'Conferimento socio',   dir: 'entrata', icon: faArrowUp,      color: '#198754' },
  { value: 'finanziamento_socio', label: 'Anticipo di denaro del socio', dir: 'entrata', icon: faArrowUp, color: '#e8590c' },
  { value: 'anticipo_socio',      label: 'Anticipo socio',       dir: 'uscita',  icon: faArrowDown,    color: '#e8590c' },
  { value: 'rimborso_socio',      label: 'Rimborso socio',       dir: 'uscita',  icon: faArrowDown,    color: '#d9480f' },
  { value: 'restituzione_socio',  label: 'Restituzione socio',   dir: 'entrata', icon: faArrowUp,      color: '#1c7ed6' },
  { value: 'rimborso_spese',      label: 'Rimborso spese',       dir: 'uscita',  icon: faArrowDown,    color: '#fd7e14' },
  { value: 'giroconto',           label: 'Giroconto banca/cassa',dir: 'neutro',  icon: faExchangeAlt,  color: '#6610f2' },
  { value: 'altro_incasso',       label: 'Altro incasso',        dir: 'entrata', icon: faArrowUp,      color: '#0dcaf0' },
  { value: 'altra_uscita',        label: 'Altra uscita',         dir: 'uscita',  icon: faArrowDown,    color: '#6c757d' },
];

const TIPO_MAP = Object.fromEntries(TIPI.map(t => [t.value, t]));

const CASSA_NOTE = {
  prelievo_socio:     'Partita soci — non incide sul reddito',
  conferimento_socio: 'Rilevante ai fini cassa → incasso',
  finanziamento_socio: 'Partita soci — debito verso il socio, non è un incasso',
  anticipo_socio:     'Rilevante ai fini cassa → pagamento',
  rimborso_socio:     'Partita soci — la cassa è già contata all’anticipo',
  restituzione_socio: 'Partita soci — non incide sul reddito',
  rimborso_spese:     'Rilevante ai fini cassa → pagamento',
  giroconto:          'Partita di giro — non incide sul reddito',
  altro_incasso:      'Rilevante ai fini cassa → incasso',
  altra_uscita:       'Rilevante ai fini cassa → pagamento',
};

const CONTI_PREDEFINITI = {
  prelievo_socio:     { dare: 'Soci c/prelievi', avere: 'Cassa / Banca' },
  conferimento_socio: { dare: 'Cassa / Banca',   avere: 'Soci c/apporti' },
  finanziamento_socio: { dare: 'Cassa / Banca',  avere: 'Soci c/anticipi' },
  anticipo_socio:     { dare: 'Spese anticipate', avere: 'Soci c/anticipi' },
  rimborso_socio:     { dare: 'Soci c/anticipi',  avere: 'Cassa / Banca' },
  restituzione_socio: { dare: 'Cassa / Banca',    avere: 'Soci c/prelievi' },
  rimborso_spese:     { dare: 'Spese rimborso',  avere: 'Cassa / Banca' },
  giroconto:          { dare: '',                avere: '' },
  altro_incasso:      { dare: 'Cassa / Banca',   avere: 'Proventi diversi' },
  altra_uscita:       { dare: 'Oneri diversi',   avere: 'Cassa / Banca' },
};

// ─── Form scrittura ─────────────────────────────────────────────────────────

function ScritturaModal({ scrittura, onClose, onSave }) {
  const isEdit = !!scrittura;
  const [form, setForm] = useState({
    data: scrittura?.data || new Date().toISOString().split('T')[0],
    tipo: scrittura?.tipo || 'prelievo_socio',
    descrizione: scrittura?.descrizione || '',
    importo: scrittura?.importo ?? '',
    conto_dare: scrittura?.conto_dare || '',
    conto_avere: scrittura?.conto_avere || '',
    socio: scrittura?.socio || '',
    note: scrittura?.note || '',
  });
  const [saving, setSaving] = useState(false);
  const [expenseAccounts, setExpenseAccounts] = useState([]);  // nomi conti spesa Firefly
  const [bankAccounts, setBankAccounts] = useState([]);        // {denominazione, firefly_asset_account_name}

  useEffect(() => {
    Promise.all([
      getFireflyAccountMapping().catch(() => null),
      getBanche(0, 200).catch(() => null),
    ]).then(([mappingRes, bancheRes]) => {
      // Il mapping ritorna un oggetto singolo (non array)
      const m = Array.isArray(mappingRes?.data) ? mappingRes.data[0] : mappingRes?.data;
      if (m) {
        setExpenseAccounts([
          m.expense_film_acquisto_account_name,
          m.expense_musica_acquisto_account_name,
          m.expense_materiali_servizi_riversamenti_account_name,
          m.expense_spese_imballaggio_account_name,
          m.expense_marketing_pubblicita_account_name,
          m.expense_spese_spedizioni_account_name,
          m.expense_commissioni_marketplace_account_name,
          m.expense_commissioni_bancarie_account_name,
        ].filter(Boolean));
      }
      if (bancheRes?.data) {
        const items = Array.isArray(bancheRes.data)
          ? bancheRes.data
          : (bancheRes.data.items || bancheRes.data.banche || []);
        setBankAccounts(items.filter(b => b.firefly_asset_account_name));
      }
    });
  }, []);

  // Opzioni datalist per Conto Dare in base al tipo
  const getDareOptions = (tipo) => {
    if (['altra_uscita', 'rimborso_spese'].includes(tipo)) return expenseAccounts;
    if (['giroconto', 'conferimento_socio', 'finanziamento_socio'].includes(tipo))
      return ['Cassa', ...bankAccounts.map(b => b.firefly_asset_account_name)];
    return [];
  };

  // Opzioni datalist per Conto Avere in base al tipo
  const getAvereOptions = (tipo) => {
    if (['altra_uscita', 'rimborso_spese', 'prelievo_socio', 'giroconto'].includes(tipo))
      return ['Cassa', ...bankAccounts.map(b => b.firefly_asset_account_name)];
    return [];
  };

  const tipoConf = TIPO_MAP[form.tipo] || TIPI[0];
  const mostraSocio = ['prelievo_socio', 'conferimento_socio', 'rimborso_spese', 'anticipo_socio', 'finanziamento_socio', 'rimborso_socio', 'restituzione_socio'].includes(form.tipo);

  const handleTipoChange = (tipo) => {
    // Default conti: usa primo conto Firefly disponibile se c'è, altrimenti testo generico
    const firstExpense = expenseAccounts[0] || CONTI_PREDEFINITI[tipo]?.dare || '';
    const firstBank = bankAccounts[0]?.firefly_asset_account_name || CONTI_PREDEFINITI[tipo]?.avere || '';
    const presets = {
      prelievo_socio:     { dare: 'Soci c/prelievi', avere: firstBank },
      conferimento_socio: { dare: firstBank,          avere: 'Soci c/apporti' },
      finanziamento_socio: { dare: firstBank,          avere: 'Soci c/anticipi' },
      anticipo_socio:     { dare: firstExpense,        avere: 'Soci c/anticipi' },
      rimborso_socio:     { dare: 'Soci c/anticipi',   avere: firstBank },
      restituzione_socio: { dare: firstBank,           avere: 'Soci c/prelievi' },
      rimborso_spese:     { dare: firstExpense,        avere: firstBank },
      giroconto:          { dare: firstBank,           avere: '' },
      altro_incasso:      { dare: firstBank,           avere: 'Proventi diversi' },
      altra_uscita:       { dare: firstExpense,        avere: firstBank },
    };
    const preset = presets[tipo] || { dare: '', avere: '' };
    setForm(f => ({
      ...f,
      tipo,
      conto_dare: preset.dare,
      conto_avere: preset.avere,
      socio: ['prelievo_socio', 'conferimento_socio', 'rimborso_spese', 'anticipo_socio', 'finanziamento_socio', 'rimborso_socio', 'restituzione_socio'].includes(tipo) ? f.socio : '',
    }));
  };

  const handleSave = async () => {
    if (!form.descrizione || !form.importo || !form.data) {
      toast.error('Data, descrizione e importo sono obbligatori');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        importo: parseFloat(form.importo),
        socio: form.socio || null,
        conto_dare: form.conto_dare || null,
        conto_avere: form.conto_avere || null,
        note: form.note || null,
      };
      if (isEdit) {
        await aggiornaScritturaContabile(scrittura.id, payload);
        toast.success('Scrittura aggiornata');
      } else {
        await creaScritturaContabile(payload);
        toast.success('Scrittura registrata');
      }
      onSave();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6">
          <FontAwesomeIcon icon={isEdit ? faEdit : faPlus} className="me-2" />
          {isEdit ? 'Modifica scrittura' : 'Nuova scrittura contabile'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          {/* Tipo */}
          <Col md={12}>
            <Form.Label className="small fw-bold">Tipo operazione</Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {TIPI.map(t => (
                <Button
                  key={t.value}
                  size="sm"
                  variant={form.tipo === t.value ? 'dark' : 'outline-secondary'}
                  style={form.tipo === t.value ? { backgroundColor: t.color, borderColor: t.color } : {}}
                  onClick={() => handleTipoChange(t.value)}
                >
                  <FontAwesomeIcon icon={t.icon} className="me-1" />
                  {t.label}
                </Button>
              ))}
            </div>
            <small className="text-muted mt-1 d-block">{CASSA_NOTE[form.tipo]}</small>
          </Col>

          {/* Data + Importo */}
          <Col md={4}>
            <Form.Label className="small">Data *</Form.Label>
            <Form.Control size="sm" type="date" value={form.data}
              onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
          </Col>
          <Col md={4}>
            <Form.Label className="small">Importo (€) *</Form.Label>
            <Form.Control size="sm" type="number" step="0.01" min="0"
              value={form.importo}
              onChange={e => setForm(f => ({ ...f, importo: e.target.value }))}
              style={{ fontWeight: 'bold', color: tipoConf.color }} />
          </Col>
          {mostraSocio && (
            <Col md={4}>
              <Form.Label className="small">Socio</Form.Label>
              <Form.Select size="sm" value={form.socio}
                onChange={e => setForm(f => ({ ...f, socio: e.target.value }))}>
                <option value="">— nessuno —</option>
                <option value="Giorgio">Giorgio</option>
                <option value="Francesca">Francesca</option>
              </Form.Select>
            </Col>
          )}

          {/* Descrizione */}
          <Col md={12}>
            <Form.Label className="small">Descrizione *</Form.Label>
            <Form.Control size="sm" type="text" value={form.descrizione}
              onChange={e => setForm(f => ({ ...f, descrizione: e.target.value }))}
              placeholder={`es. ${tipoConf.label} ${new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}`} />
          </Col>

          {/* Partita doppia con dropdown Firefly o testo libero */}
          {['conto_dare', 'conto_avere'].map(field => {
            const opts = field === 'conto_dare'
              ? getDareOptions(form.tipo)
              : getAvereOptions(form.tipo);
            const label = field === 'conto_dare' ? 'Conto Dare' : 'Conto Avere';
            const LIBERO = '__libero__';
            const isLibero = !opts.includes(form[field]) && opts.length > 0;
            return (
              <Col md={6} key={field}>
                <Form.Label className="small">{label}</Form.Label>
                {opts.length > 0 ? (
                  <>
                    <Form.Select
                      size="sm"
                      value={isLibero ? LIBERO : (form[field] || '')}
                      onChange={e => {
                        const v = e.target.value;
                        if (v !== LIBERO) setForm(f => ({ ...f, [field]: v }));
                        else setForm(f => ({ ...f, [field]: '' }));
                      }}
                    >
                      <option value="">— seleziona —</option>
                      {opts.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                      <option value={LIBERO}>✏️ Scrivi manualmente…</option>
                    </Form.Select>
                    {isLibero && (
                      <Form.Control
                        size="sm" type="text" className="mt-1"
                        value={form[field]}
                        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                        placeholder="Conto personalizzato…"
                        autoFocus
                      />
                    )}
                  </>
                ) : (
                  <Form.Control
                    size="sm" type="text"
                    value={form[field]}
                    onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                    placeholder={CONTI_PREDEFINITI[form.tipo]?.[field === 'conto_dare' ? 'dare' : 'avere'] || ''}
                  />
                )}
              </Col>
            );
          })}

          {/* Note */}
          <Col md={12}>
            <Form.Label className="small">Note</Form.Label>
            <Form.Control as="textarea" rows={2} size="sm" value={form.note}
              onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Annulla</Button>
        <Button
          style={{ backgroundColor: tipoConf.color, borderColor: tipoConf.color }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <Spinner size="sm" className="me-1" /> : <FontAwesomeIcon icon={faPlus} className="me-1" />}
          {isEdit ? 'Salva modifiche' : 'Registra'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Componente principale ──────────────────────────────────────────────────

export default function PrimaNota() {
  const [anno, setAnno] = useState(ANNO_CORRENTE);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [riepilogo, setRiepilogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [searchParams] = useSearchParams();

  // Link dalla dashboard mobile (badge "Contabilità"): apre subito il form di nuova scrittura
  useEffect(() => {
    if (searchParams.get('nuovo') === '1') {
      setEditTarget(null);
      setShowForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, rieRes] = await Promise.all([
        getPrimaNota(anno, filtroTipo || undefined),
        getRiepilogoPrimaNota(anno),
      ]);
      setRows(listRes.data.items);
      setTotal(listRes.data.total);
      setRiepilogo(rieRes.data);
    } catch {
      toast.error('Errore nel caricamento prima nota');
    } finally {
      setLoading(false);
    }
  }, [anno, filtroTipo]);

  useEffect(() => { load(); }, [load]);

  const handleElimina = async (sc) => {
    if (!window.confirm(`Eliminare "${sc.descrizione}"?`)) return;
    try {
      await eliminaScritturaContabile(sc.id);
      toast.success('Scrittura eliminata');
      load();
    } catch {
      toast.error('Errore nella eliminazione');
    }
  };

  const totaleEntrate = rows.filter(r => TIPO_MAP[r.tipo]?.dir === 'entrata')
    .reduce((s, r) => s + r.importo, 0);
  const totaleUscite = rows.filter(r => TIPO_MAP[r.tipo]?.dir === 'uscita')
    .reduce((s, r) => s + r.importo, 0);

  return (
    <div className="container-fluid py-3">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FontAwesomeIcon icon={faUserTie} className="me-2 text-primary" />
            Prima Nota
          </h4>
          <small className="text-muted">Prelievi soci · Conferimenti · Rimborsi spese · Giroconti</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Form.Select value={anno} onChange={e => setAnno(+e.target.value)} style={{ width: 100 }}>
            {[ANNO_CORRENTE, ANNO_CORRENTE - 1, ANNO_CORRENTE + 1].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </Form.Select>
          <Button variant="outline-primary" size="sm" onClick={load} title="Aggiorna">
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="primary" size="sm" onClick={() => { setEditTarget(null); setShowForm(true); }}>
            <FontAwesomeIcon icon={faPlus} className="me-1" />Nuova scrittura
          </Button>
        </Col>
      </Row>

      {/* KPI */}
      {riepilogo && (
        <Row className="g-2 mb-3">
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-danger bg-opacity-10 py-2">
              <div className="small text-danger">Prelievi soci</div>
              <div className="fw-bold text-danger">{fmt(riepilogo.totale_prelievi_soci)}</div>
              <div style={{ fontSize: '0.7rem' }} className="text-muted">
                G: {fmt(riepilogo.prelievi_giorgio)} · F: {fmt(riepilogo.prelievi_francesca)}
              </div>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-success bg-opacity-10 py-2">
              <div className="small text-success">Conferimenti</div>
              <div className="fw-bold text-success">{fmt(riepilogo.totale_conferimenti_soci)}</div>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-warning bg-opacity-10 py-2">
              <div className="small text-warning">Rimborsi spese</div>
              <div className="fw-bold text-warning">{fmt(riepilogo.totale_rimborsi_spese)}</div>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-info bg-opacity-10 py-2">
              <div className="small text-info">Altri incassi</div>
              <div className="fw-bold text-info">{fmt(riepilogo.totale_altri_incassi)}</div>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-secondary bg-opacity-10 py-2">
              <div className="small text-secondary">Altre uscite</div>
              <div className="fw-bold text-secondary">{fmt(riepilogo.totale_altre_uscite)}</div>
            </Card>
          </Col>
          <Col xs={6} md={2}>
            <Card className="text-center border-0 bg-primary bg-opacity-10 py-2">
              <div className="small text-primary">Giroconti</div>
              <div className="fw-bold text-primary">{fmt(riepilogo.totale_giroconti)}</div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filtri */}
      <Row className="g-2 mb-3 align-items-center">
        <Col xs="auto">
          <FontAwesomeIcon icon={faFilter} className="text-muted me-1" />
        </Col>
        <Col xs="auto">
          <Form.Select size="sm" value={filtroTipo}
            onChange={e => setFiltroTipo(e.target.value)} style={{ width: 200 }}>
            <option value="">Tutti i tipi</option>
            {TIPI.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Form.Select>
        </Col>
        {filtroTipo && (
          <Col xs="auto">
            <Button size="sm" variant="outline-secondary" onClick={() => setFiltroTipo('')}>
              Reimposta
            </Button>
          </Col>
        )}
        <Col xs="auto" className="ms-auto">
          <small className="text-muted me-3">
            <span className="text-success fw-bold">+{fmt(totaleEntrate)}</span>
            {' · '}
            <span className="text-danger fw-bold">-{fmt(totaleUscite)}</span>
            {' · '}
            {total} righe
          </small>
        </Col>
      </Row>

      {loading && <div className="text-center py-5"><Spinner /></div>}

      {!loading && rows.length === 0 && (
        <Alert variant="info">
          Nessuna scrittura per {anno}
          {filtroTipo ? ` (filtro: ${TIPO_MAP[filtroTipo]?.label})` : ''}.
          Usa "Nuova scrittura" per registrare prelievi soci, giroconti o altri movimenti.
        </Alert>
      )}

      {!loading && rows.length > 0 && (
        <Table responsive bordered hover size="sm">
          <thead className="table-dark">
            <tr>
              <th>Data</th>
              <th>Tipo</th>
              <th>Descrizione</th>
              <th>Socio</th>
              <th>Dare</th>
              <th>Avere</th>
              <th className="text-end">Importo</th>
              <th>Cassa</th>
              <th style={{ width: 80 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const t = TIPO_MAP[r.tipo] || TIPI[5];
              return (
                <tr key={r.id}>
                  <td className="small">{fmtDate(r.data)}</td>
                  <td>
                    <span style={{ color: t.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      <FontAwesomeIcon icon={t.icon} className="me-1" />
                      {t.label}
                    </span>
                  </td>
                  <td className="small">{r.descrizione}</td>
                  <td className="small">{r.socio || '—'}</td>
                  <td className="small text-muted">{r.conto_dare || '—'}</td>
                  <td className="small text-muted">{r.conto_avere || '—'}</td>
                  <td className="text-end fw-bold" style={{ color: t.color }}>
                    {t.dir === 'entrata' ? '+' : t.dir === 'uscita' ? '-' : ''}
                    {fmt(r.importo)}
                  </td>
                  <td className="text-center">
                    {r.rilevante_ai_fini_cassa
                      ? <Badge bg={r.tipo_cassa === 'incasso' ? 'success' : 'danger'} style={{ fontSize: '0.7rem' }}>
                          {r.tipo_cassa}
                        </Badge>
                      : <span className="text-muted small">—</span>}
                  </td>
                  <td>
                    <Button size="sm" variant="outline-secondary" className="me-1"
                      onClick={() => { setEditTarget(r); setShowForm(true); }}>
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button size="sm" variant="outline-danger"
                      onClick={() => handleElimina(r)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {showForm && (
        <ScritturaModal
          scrittura={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          onSave={() => { setShowForm(false); setEditTarget(null); load(); }}
        />
      )}
    </div>
  );
}
